import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import { fetchStateData, updateDistrict } from "../Services/APIService";

interface viewDatas {
  stateId: number;
  stateName: string;
  isActive: boolean;
  districtId: number;
  districtName: string;
}

interface UpdateCardProps {
  viewData: viewDatas | null;
  onUpdateComplete: () => void;
  onCancel: () => void;
}

interface FormData {
  districtId: number;
  districtName: string;
  isActive: boolean;
  stateId: number | null;
}

interface State {
  stateId: number;
  stateName: string;
  isActive: boolean;
}

const UpdateCard: React.FC<UpdateCardProps> = ({
  viewData,
  onUpdateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    districtId: 0,
    districtName: "",
    isActive: true,
    stateId: null,
  });

  const [states, setStates] = useState<State[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const requiredField = <span className="text-danger ms-1">*</span>;

  // Fetch states on component mount
  useEffect(() => {
    const getStates = async () => {
      setIsLoadingStates(true);
      try {
        const stateData = await fetchStateData();
        setStates(stateData.filter((state) => state.isActive));
      } catch (error) {
        setError("Failed to fetch states");
        console.error(error);
      } finally {
        setIsLoadingStates(false);
      }
    };

    getStates();
  }, []);

  // Set form data when viewData changes
  useEffect(() => {
    if (viewData) {
      setFormData({
        districtId: viewData.districtId,
        districtName: viewData.districtName,
        isActive: viewData.isActive,
        stateId: viewData.stateId,
      });
    }
  }, [viewData]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "stateId" ? parseInt(value) : value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.districtName.trim()) {
      setError("Please enter a valid District Name");
      return;
    }

    try {
      setIsLoading(true);

      // Get user ID from localStorage
      const userId = localStorage.getItem("UserId");

      if (!userId) {
        throw new Error("User ID not found in localStorage");
      }

      // Prepare request payload according to specified format
      const payload = {
        districtID: formData.districtId,
        districtName: formData.districtName.trim().replace(/\s+/g, " "),
        isActive: formData.isActive,
        modifieddBy: parseInt(userId),
      };

      const result = await updateDistrict(payload);
      console.log("Response from updateDistrict:", result);
      if (result.isUpdated) {
        toast.success("District Updated Successfully!");
        onUpdateComplete();
      } else {
        toast.error(result.message || "District update failed!");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      districtName: "",
      districtId: 0,
      isActive: true,
      stateId: null,
    });
    setError(null);
    // Call the cancel function
    onCancel();
  };

  return (
    <div className="card">
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Update District</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="districtId" className="form-label">
                District ID
              </label>
              <input
                type="number"
                id="districtId"
                name="districtId"
                className="form-control form-control-lg"
                placeholder="District ID"
                value={formData.districtId}
                disabled // Disable editing of District ID
                required
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="stateId" className="form-label">
                State Name
              </label>
              {isLoadingStates ? (
                <div className="text-muted">Loading states...</div>
              ) : (
                <select
                  id="stateId"
                  name="stateId"
                  className="form-select form-control-lg"
                  value={formData.stateId ?? ""}
                  disabled
                  required
                >
                  {states.length === 0 ? (
                    <option value="" disabled>
                      No states available
                    </option>
                  ) : (
                    states.map((state) => (
                      <option key={state.stateId} value={state.stateId}>
                        {state.stateName}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label htmlFor="districtName" className="form-label">
                District Name{requiredField}
              </label>
              <input
                type="text"
                id="districtName"
                name="districtName"
                className="form-control form-control-lg"
                placeholder="District Name"
                value={formData.districtName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <label className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleCheckboxChange}
                />
                <span className="form-check-label">Active</span>
              </label>
            </div>

            <div className="col-12 mt-2">
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateCard;
